// The transport: play, step, now, release — the one place time is driven from.
//
// Until this existed the playhead could only be moved by hovering a chart or by
// the reach field's own scrubber, which meant the timeline could not be played at
// all unless a wind field happened to be loaded. Time is not a property of the
// field; it belongs to the whole console, so the control belongs to the stack.
//
// This is also why the field's on-map bar keeps only its readout and legend now.
// Two play buttons stepping two different grids (the charts are hourly; the field
// coarsens to 3-hourly past f120) is the same "which one is real" confusion the
// two competing layer controls used to cause.
//
// Everything here goes through the shared cursor. The transport does not tell any
// chart or the map anything directly — it moves the instant, and the surfaces that
// care are already subscribed.

import {
  getCursor,
  indexAtTime,
  setCursor,
  subscribeCursor,
  timeAtIndex,
  timesOf,
} from "../time/cursor.js";
import { fmtDayDateHM, subscribeTimeMode } from "../ui/time.js";

/** Milliseconds of wall clock per forecast hour while playing. A full 9-day span
 *  is ~215 steps, so this runs it in about twenty seconds — fast enough to watch a
 *  system cross, slow enough to see a limit breach arrive. */
const PLAY_MS = 95;

export interface Transport {
  /** Point the transport at a forecast's hours. `null` disables it. */
  update(xLabels: readonly string[] | null): void;
  destroy(): void;
}

function fmt(t: number): string {
  // Through the shared clock (ui/time.ts): site-local by default, matching the
  // zone the labels parse in — the browser's zone here would name a different
  // hour than the chart tick under the playhead whenever the operator is not in
  // the site's timezone — and explicitly-labelled UTC when toggled.
  return fmtDayDateHM(t);
}

function btn(cls: string, label: string, title: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = `tp-btn ${cls}`;
  b.textContent = label;
  b.title = title;
  return b;
}

/** True when a keystroke belongs to whatever the user is typing in. */
function typingInto(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export function createTransport(host: HTMLElement): Transport {
  let labels: readonly string[] | null = null;
  let timer: number | null = null;
  // "This cursor change came from us" — so the subscription below can ignore its
  // own echo instead of treating it as a scrub elsewhere and stopping playback.
  let publishing = false;

  const root = document.createElement("div");
  root.className = "transport";

  const bPlay = btn("tp-play", "▶", "Play / pause the timeline");
  const bPrev = btn("tp-step", "‹", "Back one hour (←)");
  const bNext = btn("tp-step", "›", "Forward one hour (→)");
  const bNow = btn("tp-now", "now", "Jump to the present hour");
  const bClear = btn("tp-clear", "clear", "Release the playhead");
  const readout = document.createElement("span");
  readout.className = "tp-time";
  root.append(bPlay, bPrev, bNext, readout, bNow, bClear);
  host.appendChild(root);

  const publish = (t: number | null): void => {
    publishing = true;
    setCursor(t);
    publishing = false;
  };

  /** The index the playhead is on, or the present hour when it is released. */
  const currentIndex = (): number => {
    if (labels === null) return 0;
    const i = indexAtTime(labels, getCursor());
    if (i !== null) return i;
    return nowIndex();
  };

  const nowIndex = (): number => {
    if (labels === null) return 0;
    const times = timesOf(labels);
    const now = Date.now();
    // Nearest sample to the present moment, clamped into the span: a console open
    // on an old forecast should land at its last hour, not silently at hour zero.
    let best = 0;
    let bestGap = Infinity;
    for (let i = 0; i < times.length; i++) {
      const gap = Math.abs((times[i] ?? NaN) - now);
      if (gap < bestGap) {
        bestGap = gap;
        best = i;
      }
    }
    return best;
  };

  const step = (delta: number): void => {
    if (labels === null || labels.length === 0) return;
    const i = Math.max(0, Math.min(labels.length - 1, currentIndex() + delta));
    publish(timeAtIndex(labels, i));
  };

  const stopPlay = (): void => {
    if (timer !== null) {
      window.clearInterval(timer);
      timer = null;
    }
    bPlay.textContent = "▶";
    bPlay.classList.remove("on");
  };

  const startPlay = (): void => {
    if (labels === null || labels.length < 2) return;
    bPlay.textContent = "❚❚";
    bPlay.classList.add("on");
    timer = window.setInterval(() => {
      if (labels === null) return stopPlay();
      const i = currentIndex();
      // Wrap rather than stop at the end: watching a system arrive twice is the
      // normal way people read one of these, and a transport that halts on the
      // last frame makes you reach for the mouse to see it again.
      publish(timeAtIndex(labels, i + 1 >= labels.length ? 0 : i + 1));
    }, PLAY_MS);
  };

  bPlay.addEventListener("click", () => (timer === null ? startPlay() : stopPlay()));
  bPrev.addEventListener("click", () => {
    stopPlay();
    step(-1);
  });
  bNext.addEventListener("click", () => {
    stopPlay();
    step(1);
  });
  bNow.addEventListener("click", () => {
    stopPlay();
    if (labels !== null) publish(timeAtIndex(labels, nowIndex()));
  });
  bClear.addEventListener("click", () => {
    stopPlay();
    publish(null);
  });

  const onKey = (ev: KeyboardEvent): void => {
    if (ev.altKey || ev.ctrlKey || ev.metaKey || typingInto(ev.target)) return;
    if (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight") return;
    ev.preventDefault();
    stopPlay();
    step(ev.key === "ArrowRight" ? 1 : -1);
  };
  document.addEventListener("keydown", onKey);

  const paint = (): void => {
    const t = getCursor();
    readout.textContent = t === null ? "—" : fmt(t);
    readout.classList.toggle("idle", t === null);
    const off = labels === null || labels.length < 2;
    for (const b of [bPlay, bPrev, bNext, bNow, bClear]) b.disabled = off;
  };

  const unsubscribe = subscribeCursor(() => {
    // A scrub from anywhere else (a chart hover, the map) takes over from the
    // animation — but only when it really came from elsewhere.
    if (!publishing) stopPlay();
    paint();
  });
  const unsubClock = subscribeTimeMode(paint);
  paint();

  return {
    update(xLabels): void {
      stopPlay();
      labels = xLabels !== null && xLabels.length > 0 ? xLabels : null;
      root.hidden = labels === null;
      paint();
    },
    destroy(): void {
      stopPlay();
      unsubscribe();
      unsubClock();
      document.removeEventListener("keydown", onKey);
      root.remove();
    },
  };
}
